import type { Academy } from '~/@entities/Academy'
import type {
  CommonResponse,
  CommonResponseSliceInquiryResponse,
  ContentBugReportPostRequest,
  GenerateAttachmentPresignedUrlsData,
  GetActiveInquiriesByParams,
  InquiryAttachmentPresignedUrlRequest,
  InquiryInsertRequest,
  PostProblemBugReportData,
} from '~/api-spec/__generated__/main'
import { httpClient, type RequestMeta } from '~/http/client'
import type { Slice } from '~/schema/slice'

import { adaptInquiries, adaptInquiryAttachmentPresignedUrls } from './adapters'
import type {
  ContentBugReportCategory,
  ContentBugReportSubtype,
  ContentBugReportType,
  ContractContactIncomingPath,
  Inquiry,
  InquiryAttachmentPresignedUrl,
} from './schemas'

export type InquiryListParams = GetActiveInquiriesByParams
export type InquiryCreateBody = InquiryInsertRequest
export type InquiryAttachmentBody = NonNullable<
  NonNullable<InquiryCreateBody['attachments']>[number]
>
export type InquiryAttachmentPresignedUrlBody = InquiryAttachmentPresignedUrlRequest

export const getInquiries = async (
  params: InquiryListParams,
  meta?: RequestMeta,
): Promise<Slice<Inquiry>> => {
  const body = await httpClient().get<CommonResponseSliceInquiryResponse>('/inquires', {
    params,
    meta,
  })
  return adaptInquiries(body.data)
}

export const createInquiry = async (inquiry: InquiryCreateBody, meta?: RequestMeta) => {
  await httpClient().post<CommonResponse>('/inquires', inquiry, { meta })
}

export const deleteInquiries = async (ids: Inquiry['id'][], meta?: RequestMeta) => {
  await httpClient().delete<CommonResponse>('/inquires', { data: ids, meta })
}

// 문의 API 중 이 주소만 CommonResponse 봉투 없이 본문을 그대로 낸다.
export const createInquiryAttachmentPresignedUrls = async (
  files: InquiryAttachmentPresignedUrlBody,
  signal?: AbortSignal,
): Promise<InquiryAttachmentPresignedUrl[]> => {
  const body = await httpClient().post<GenerateAttachmentPresignedUrlsData>(
    '/inquires/attachments/presigned-urls',
    files,
    { signal },
  )
  return adaptInquiryAttachmentPresignedUrls(body)
}

/***************** 콘텐츠 오류 신고 *****************/
// 생성 타입은 서버의 제네릭 enum(type·subtype)을 이름·순번을 가진 객체로 잘못 그린다. 실제로는 enum 이름 문자열을
// 받으므로 두 필드만 도메인 어휘로 바꾼다. 원본 교재 정보(additionalInfo)는 문제은행 문제 신고만 저장하고
// 나머지는 무시한다. 2026-10-14 백엔드 develop(6a0f8e7bb) 의 bugReport/controller/BugReportController.kt·
// request/ContentBugReportPostRequest.kt 로 확인.
export type ContentBugReportBody = Omit<ContentBugReportPostRequest, 'type' | 'subtype'> & {
  type: ContentBugReportType
  subtype: ContentBugReportSubtype
}

const CONTENT_BUG_REPORT_PATH = {
  PROBLEM: '/bug-report/content/problem',
  PUBLIC_PROBLEM: '/bug-report/content/public-problem',
  CONCEPT: '/bug-report/content/concept',
  LITTLE_CHAPTER: '/bug-report/content/little-chapter',
  WORKSHEET: '/bug-report/content/worksheet',
} as const satisfies Record<ContentBugReportCategory, string>

export const createContentBugReport = async (
  category: ContentBugReportCategory,
  report: ContentBugReportBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post<PostProblemBugReportData>(CONTENT_BUG_REPORT_PATH[category], report, {
    meta,
  })
}

/***************** 정식가입 상담 신청 *****************/
// 홈페이지 서버('homepage' 클라이언트)는 Swagger 를 내지 않고 소스도 이 저장소에서 볼 수 없어, 2026-10-14 기준 선생님 앱이 보내던
// 본문을 근거로 적었다.
export type ContractContactRequestBody = {
  academyId: Academy['academyId']
  incomingPath: ContractContactIncomingPath
}

/** 정식가입 상담을 신청한다. 응답 본문은 쓰지 않는다 */
export const requestContractContact = async (
  body: ContractContactRequestBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient('homepage').post<unknown>('/contract-contact-request', body, { meta })
}
